// don't judge me the code is kinda ugly :( - ianr

//npm run deploy -- --m "v# --commit message"
import './App.css';
import React from 'react';
import { app } from './functions/firebaseInit';
import { initializeApp } from "firebase/app";
import { createBrowserRouter, Outlet, RouterProvider, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import CupBuilder from './components/pages/CupBuilder/CupBuilder';
import SignUp from './components/pages/auth/SignUp';
import SignIn from './components/pages/auth/SignIn';
import { useState, useLayoutEffect, useEffect } from 'react';
import ForgotPassword from './components/pages/auth/ForgotPassword';
import { getFirestore, getDocs, collection, getDoc, doc } from 'firebase/firestore';
import Browse from './components/pages/Browse/Browse';
import BrowseCupId from './components/pages/Browse/BrowseCupId';
import Profile from './components/pages/Profile/Profile';
import getTracks from './functions/getTrackData';
import ProfileEdit from './components/pages/Profile/ProfileEdit';
import Header from './components/Header/Header';

function App() {

  const [, setAuth] = useState(false); // force state update on auth change
  const fbApp = initializeApp(app);
  const auth = getAuth(fbApp);
  const firestore = getFirestore(fbApp);

  // persistent login more consistently
  useLayoutEffect(() => {

    const listener = onAuthStateChanged(auth, async (user) => {
      setAuth(!!user);
    });

    return () => { listener(); }
  }, [auth]);

  // render header/footer on applicable pages
  const Layout = () => {

    // handles screen size to adjust appearance of cups table
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
      const handleResize = () => {
        setWidth(window.innerWidth);
      }
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      }
    }, []);

    return <>
      <Header width={width} />
      <Outlet context={[width, auth, fbApp]} />
      <footer>
        <div id="footer-topline" />

        <div id="footer-content">
          <h4>Mario Kart 8 Deluxe Custom Cup Builder</h4>
          <p>Found a bug? An inappropriate name? Or you have feedback? Contact me here:</p>
          <ul>
            <li>Email: <a href="mailto:mk8dxcupbuilder@gmail.com">mk8dxcupbuilder@gmail.com</a></li>
            <li><code>ian_._</code> on Discord</li>
            <li><a href="https://channel3.gg/ianr" rel="noopener" target='_noblank'>@ianr</a> on Channel 3</li>
            <li><a href="https://twitter.com/ian_jr926" rel="noopener" target='_noblank'>@ian_jr926</a> on Twitter</li>
          </ul>

          <p>
            All Mario Kart 8 Deluxe licensed assets used (original or edited in
            any way) belong to Nintendo and were sourced from <a
              href="https://mariokart8.nintendo.com/"
              target='_blank' rel='noreferrer'>
              Mario Kart 8 Deluxe's official website</a>.
            I am not using these assets for profit, just for fun.
          </p>
        </div>
      </footer>
    </>
  }

  const router = createBrowserRouter([
    {
      path: "*",
      element: <NotFound />
    },
    {
      path: "/signin",
      element: <SignIn />,
    },
    {
      path: "/signup",
      element: <SignUp />
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword />
    },
    {
      element: <Layout />,
      children: [
        {
          path: "/",
          element: <CupBuilder />
        },
        {
          path: "/browse",
          element: <Browse />,
          loader: async () => {
            const response = await getDocs(collection(firestore, "cups"));
            const cupData = [];
            response.forEach((doc) => {
              const docData = doc.data();
              cupData.push({ id: doc.id, ...doc.data(), tracks: getTracks(docData.tracks) })
            });
            return cupData;
          }
        },
        {
          path: "/browse/cup/:cupid",
          element: <BrowseCupId />,
          errorElement: <div id="main-container"><p>Cup at this URL not found.</p></div>,
          loader: async ({ params }) => {
            const cup = await getDoc(doc(firestore, "cups", params.cupid));
            if (cup.exists()) { // don't find creator if invalid cup
              const cupData = cup.data();
              const user = await getDoc(doc(firestore, "users", cupData.creator));
              const userData = user.data();

              return {
                id: cup?.id,
                tracks: getTracks(cupData.tracks),
                creator: userData.displayName,
                creatorId: cupData.creator,
                cupName: cupData.cupName
              };

            } else {  // throw errorElement if invalid cup
              throw new Error("no cup found");
            }
          }
        },
        {
          path: "/profile/:uid",
          element: <Profile />,
          errorElement: <div id="main-container"><p>Profile not found at this URL.</p></div>,
          loader: async ({ params }) => {
            const userDoc = await getDoc(doc(firestore, "users", params.uid));
            const userData = userDoc.data();
            const cups = [];
            // check if valid user before getting cups, saves a read on invalid URLs.
            if (userDoc.exists() && userData.cups) {
              for (const cup of userData.cups) {
                const cupDoc = await getDoc(cup);
                const data = cupDoc.data();
                cups.push({ id: cupDoc.id, tracks: getTracks(data.tracks), cupName: data.cupName });
              }
            } else {  // throw errorElement if no profile found
              throw new Error("no profile found");
            }
            return { ...userData, cups: cups, id: userDoc.id };
          }
        },
        {
          path: "/profile/:uid/edit",
          element: <ProfileEdit />,
          loader: async ({ params }) => {
            const userDoc = await getDoc(doc(firestore, "users", params.uid));
            const userData = userDoc.data();
            const cups = [];
            if (userDoc.exists() && userData.cups.length > 0) {  // no reason to force extra reads if invalid user
              for (const cup of userData.cups) {
                const cupDoc = await getDoc(cup);
                const data = cupDoc.data();
                cups.push({ id: cupDoc.id, tracks: getTracks(data.tracks), cupName: data.cupName });
              }
            }
            return { ...userData, cups: cups, id: userDoc.id };
          }
        }
      ]
    }
  ]);

  return <RouterProvider router={router} />;
}

const NotFound = () => {
  const navigate = useNavigate();
  setTimeout(() => {
    navigate("/");
  }, 5000);
  return <h1>Page not found. Redirecting to homepage</h1>
}

export default App;
